// Pure derivations over /market/forecaster-leaderboard/series payloads.
// All helpers tolerate empty/missing buckets and return [] or null.

const sameUser = (row, userId) => String(row.user_id) === String(userId);

const isReference = (row) => Boolean(row.is_ensemble || row.is_fixed_payment);

// "belgium_solar_opendata_elia" -> "Belgium Solar Opendata Elia".
// Resource records only carry their machine name; UI surfaces humanize it.
export const humanizeResourceName = (name) =>
  String(name || '')
    .replace(/[_-]+/g, ' ')
    .trim()
    .replace(/\b\w/g, (c) => c.toUpperCase());

export const paneFetchKey = ({ track, horizonOffset, year, month }) =>
  `${track}|${horizonOffset}|${year}-${month}`;

const bucketPoints = (payload, pick) =>
  (payload?.buckets || []).reduce((acc, bucket) => {
    const point = pick(bucket);
    if (point != null) acc.push({ key: bucket.key, label: bucket.label, ...point });
    return acc;
  }, []);

// Backend ranks are authoritative: the series endpoint dense-ranks
// forecasters only (fixed-payment participants and the ensemble reference
// row never shift them), matching the stored monthly stats for every role.
export const userRankSeries = (payload, userId) =>
  bucketPoints(payload, (bucket) => {
    const row = (bucket.rows || []).find((r) => !isReference(r) && sameUser(r, userId));
    return row ? { rank: row.rank } : null;
  });

export const userScoreSeries = (payload, userId) =>
  bucketPoints(payload, (bucket) => {
    const row = (bucket.rows || []).find((r) => !isReference(r) && sameUser(r, userId));
    return row ? { score: row.avg_score } : null;
  });

// Lowest field error per bucket (the day's best forecaster), excluding the
// ensemble and contracted reference rows. Powers the "vs best" comparison in
// score mode.
export const bestErrorSeries = (payload) =>
  bucketPoints(payload, (bucket) => {
    const scores = (bucket.rows || [])
      .filter((r) => !isReference(r) && r.avg_score != null)
      .map((r) => r.avg_score);
    return scores.length ? { best: Math.min(...scores) } : null;
  });

export const ensembleRankSeries = (payload) =>
  bucketPoints(payload, (bucket) => {
    const row = (bucket.rows || []).find((r) => r.is_ensemble && r.rank != null);
    return row ? { rank: row.rank } : null;
  });

// Sorted union of bucket labels across payloads, keyed by the ISO date
// string (bucket.key). CategoryScale otherwise appends unseen labels in
// dataset-encounter order, so markets with disjoint scored-day sets would
// render days out of chronological order without an explicit labels array.
export const bucketLabelUnion = (payloads) => {
  const byKey = new Map();
  (payloads || []).forEach((payload) => {
    (payload?.buckets || []).forEach((bucket) => {
      if (!byKey.has(bucket.key)) byKey.set(bucket.key, bucket.label);
    });
  });
  return [...byKey.keys()].sort().map((key) => byKey.get(key));
};

// Rank + score for one forecaster in one market, merged by bucket key so a
// single chart point can carry both for tooltips regardless of which one
// drives the y axis. Sorted chronologically.
export const mergedUserSeries = (payload, userId) => {
  const byKey = new Map();
  const put = (key, label, field, value) => {
    const point = byKey.get(key) || { key, x: label };
    point[field] = value;
    byKey.set(key, point);
  };
  userRankSeries(payload, userId).forEach((d) => put(d.key, d.label, 'rank', d.rank));
  userScoreSeries(payload, userId).forEach((d) => put(d.key, d.label, 'score', d.score));
  return [...byKey.values()].sort((a, b) => (a.key < b.key ? -1 : a.key > b.key ? 1 : 0));
};

export const monthlyStanding = (payload, userId) => {
  const errors = payload?.summary?.forecaster_avg_errors || {};
  const entries = Object.entries(errors).sort((a, b) => a[1] - b[1]);
  if (!entries.length) return null;
  const idx = entries.findIndex(([id]) => String(id) === String(userId));
  if (idx === -1) return null;
  // dense rank: ties share a rank, no gaps
  let rank = 1;
  for (let i = 1; i <= idx; i += 1) {
    if (entries[i][1] > entries[i - 1][1]) rank += 1;
  }
  return { rank, of: entries.length, avgError: entries[idx][1] };
};

export const rankDelta = (payload, userId) => {
  const series = userRankSeries(payload, userId);
  if (series.length < 2) return null;
  return series[series.length - 2].rank - series[series.length - 1].rank;
};

export const forecasterOptions = (payloads, blurSensitive) => {
  const byId = new Map();
  (payloads || []).forEach((payload) => {
    (payload?.buckets || []).forEach((bucket) => {
      (bucket.rows || []).forEach((row) => {
        if (isReference(row) || byId.has(String(row.user_id))) return;
        const label = blurSensitive
          ? (row.username || String(row.user_id))
          : (row.email || row.username || String(row.user_id));
        byId.set(String(row.user_id), { value: String(row.user_id), label });
      });
    });
  });
  return [...byId.values()].sort((a, b) => a.label.localeCompare(b.label));
};
